import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MdEdit,
  MdShare,
  MdDownload,
  MdAddCircleOutline,
  MdLink,
  MdMoreHoriz
} from 'react-icons/md';

export interface DropdownAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

export interface Action {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  dropdownActions?: DropdownAction[];
}

interface ActionBarProps {
  actions?: Action[];
  className?: string;
}

const ActionBar: React.FC<ActionBarProps> = ({ actions, className = '' }) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Default actions
  const defaultActions: Action[] = [
    {
      id: 'edit',
      icon: <MdEdit size={20} />,
      label: 'Edit',
    },
    {
      id: 'add-assets',
      icon: <MdAddCircleOutline size={20} />,
      label: 'Add Assets',
    },
    {
      id: 'share',
      icon: <MdShare size={20} />,
      label: 'Share',
    },
    {
      id: 'link',
      icon: <MdLink size={20} />,
      label: 'Link',
    },
    {
      id: 'more',
      icon: <MdMoreHoriz size={20} />,
      label: 'More',
    }
  ];

  const actionsToRender = actions || defaultActions;

  const handleActionClick = (action: Action) => {
    if (action.dropdownActions) {
      setActiveDropdown(activeDropdown === action.id ? null : action.id);
    } else if (action.onClick) {
      action.onClick();
    }
  };

  return (
    <div className={`flex justify-center ${className}`}>
      <div className="inline-flex flex-col items-center gap-3 rounded-full px-2 py-3 shadow-lg relative"
        style={{
          backdropFilter: "blur(10px)",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 0px 14px 0px",
          background: "linear-gradient(180deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))",
          border: "1px solid rgba(224, 224, 224, 0.13)"
        }}>
        {actionsToRender.map((action, index) => (
          <div
            key={action.id}
            className="relative"
            onMouseEnter={() => action.dropdownActions && setActiveDropdown(action.id)}
            onMouseLeave={() => action.dropdownActions && setActiveDropdown(null)}
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleActionClick(action)}
              className="flex cursor-pointer items-center justify-center w-10 h-10 rounded-full hover:bg-surface-medium transition-colors text-white"
              title={action.label}
            >
              {action.icon}
            </motion.button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {action.dropdownActions && activeDropdown === action.id && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-full mr-4 top-0 min-w-[200px] rounded-xl shadow-xl overflow-hidden"
                  style={{
                    backdropFilter: "blur(10px)",
                    boxShadow: "rgba(0, 0, 0, 0.1) 0px 0px 14px 0px",
                    background: "rgba(33, 33, 33, 0.95)",
                    border: "1px solid rgba(224, 224, 224, 0.13)"
                  }}
                >
                  {action.dropdownActions.map((dropdownAction) => (
                    <button
                      key={dropdownAction.id}
                      onClick={() => {
                        dropdownAction.onClick();
                        setActiveDropdown(null);
                      }}
                      className="w-full px-4 py-3 cursor-pointer flex items-center gap-3 text-white hover:bg-gray-700/50 transition-colors"
                    >
                      <span className="text-gray-300">{dropdownAction.icon}</span>
                      <span className="text-sm font-medium">{dropdownAction.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Divider - show after "Add Assets" button */}
            {action.id === 'add-assets' && index < actionsToRender.length - 1 && (
              <div className="h-px w-6 bg-gray-600 mt-3" />
            )}
          </div>
        ))}

      </div>
    </div>
  );
};

export default ActionBar;
